/* eslint-disable max-lines */
/* eslint-disable complexity */
import { useAuth0 } from '@auth0/auth0-react';
import { IconNames } from '@blueprintjs/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import { BenefitTypes } from '@strata/constants';
import { formatAddress, formatDate, formatNameLastFirst } from '@strata/helper-functions';
import { ActionButtonPageTitle, IconButton } from '@strata/react-buttons';
import { ButtonWithDropdownMenu } from '@strata/react-dropdown-menu';
import { FormHeaderAndTabbedContent } from '@strata/react-form-header-and-content';
import { InlineMessage } from '@strata/react-inline-message';
import { FormTitleLabelValuePair, HeaderLabelValuePair } from '@strata/react-label-value-pairs';
import { TreatmentStatusIndicator } from '@strata/react-status-indicators';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { EnrollmentTab } from 'components/enrollment/EnrollmentTab';
import { mapTestClaimForGrid } from 'components/treatments/claimInsightsFields';
import { AddUpdateLockDialog } from 'components/treatments/dialogs/AddUpdateLockDialog';
import { BlocklistTreatmentDialog } from 'components/treatments/dialogs/BlocklistTreatmentDialog';
import { ChangeNdcDialog } from 'components/treatments/dialogs/ChangeNdcDialog';
import { DiscontinuedTherapyTreatmentDialog } from 'components/treatments/dialogs/DiscontinuedTherapyTreatmentDialog';
import { NotificationSettingsDialog } from 'components/treatments/dialogs/NotificationSettingsDialog';
import { ReinitiateTherapyTreatmentDialog } from 'components/treatments/dialogs/ReinitiateTherapyTreatmentDialog';
import { RemanageTreatmentDialog } from 'components/treatments/dialogs/RemanageTreatmentDialog';
import { RemoveFromBlocklistDialog } from 'components/treatments/dialogs/RemoveFromBlocklistDialog';
import { RemoveLockDialog } from 'components/treatments/dialogs/RemoveLockDialog';
import { UnmanageTreatmentDialog } from 'components/treatments/dialogs/UnmanageTreatmentDialog';
import { UpdateActiveReferralDataDialog } from 'components/treatments/dialogs/UpdateActiveReferralDataDialog';
import { UpdateMemberDataDialog } from 'components/treatments/dialogs/UpdateMemberDataDialog';
import { UpdateNeedsByDateDialog } from 'components/treatments/dialogs/UpdateNeedsByDateDialog';
import { UpdatePlaceOfServiceDialog } from 'components/treatments/dialogs/UpdatePlaceOfServiceDialog';
import { LocksTab } from 'components/treatments/LocksTab';
import { ReferralCreationDecisionsTab } from 'components/treatments/ReferralCreationDecisionsTab';
import { TestClaims } from 'components/treatments/TestClaims';
import { TherapyTransitionInlineMessages } from 'components/treatments/TherapyTransitionInlineMessages';
import { TreatmentAuditHistory } from 'components/treatments/TreatmentAuditHistory';
import { TreatmentClaimsTab } from 'components/treatments/TreatmentClaimsTab';
import { TreatmentDetailOverview } from 'components/treatments/TreatmentDetailOverview';
import {
    GRANDFATHERED_REFERRAL_CONTEXT_BANNER,
    GRANDFATHERED_TREATMENT_ACTION_DISABLED_REASON,
} from 'constants/grandfatheredTreatment';
import { getCurrentReferralId } from 'services/intakeCaseHelpers';
import { useGoBack } from 'services/navigationHelpers';
import { AppRoutes } from 'services/routes';
import {
    shouldShowTherapyTransitionMessage,
    TransitionViewContext,
} from 'services/treatmentTransitionHelpers';
import { useScopeAssessorMemberDetails } from 'services/useScopeAssessorMemberDetails';
import { useGetClaimsForMemberDrugQuery } from 'store/api/claimApi';
import { useGetWatchesQuery, WatchResources } from 'store/api/notificationApi';
import { useGetOpportunityByTreatmentQuery } from 'store/api/opportunityCuratorApi';
import { useGetPBMLockStatusQuery } from 'store/api/pbmApi';
import { useGetNeedsByDateQuery } from 'store/api/performanceApi';
import { useGetAllProductServicesQuery, useGetPayerSegmentsQuery } from 'store/api/referenceApi';
import {
    useGetReferralTransitionInfoQuery,
    useGetRejectedClaimsForTreatmentQuery,
    useGetTestClaimsForTreatmentQuery,
    useGetTreatmentDetailsQuery,
} from 'store/api/treatmentApi';

/** Agora Action Dialogs Enum */
const ActionDialogs = {
    ADD_UPDATE_LOCK: 'ADD_UPDATE_LOCK',
    REMOVE_LOCK: 'REMOVE_LOCK',
    UPDATE_ACTIVE_REFERRAL_DATA: 'UPDATE_ACTIVE_REFERRAL_DATA',
    UPDATE_MEMBER_DATA: 'UPDATE_MEMBER_DATA',
    UNMANAGE_TREATMENT: 'UNMANAGE_TREATMENT',
    REMANAGE_TREATMENT: 'REMANAGE_TREATMENT',
    DISCONTINUED_THERAPY: 'DISCONTINUED_THERAPY',
    REINITIATE_THERAPY: 'REINITIATE_THERAPY',
    UPDATE_NEEDS_BY_DATE: 'UPDATE_NEEDS_BY_DATE',
    UPDATE_PLACE_OF_SERVICE: 'UPDATE_PLACE_OF_SERVICE',
    CHANGE_NDC: 'CHANGE_NDC',
    BLOCKLIST_TREATMENT: 'BLOCKLIST_TREATMENT',
    REMOVE_FROM_BLOCKLIST: 'REMOVE_FROM_BLOCKLIST',
};

const ActionDialogComponentMap = {
    [ActionDialogs.ADD_UPDATE_LOCK]: AddUpdateLockDialog,
    [ActionDialogs.REMOVE_LOCK]: RemoveLockDialog,
    [ActionDialogs.UPDATE_ACTIVE_REFERRAL_DATA]: UpdateActiveReferralDataDialog,
    [ActionDialogs.UPDATE_MEMBER_DATA]: UpdateMemberDataDialog,
    [ActionDialogs.UPDATE_PLACE_OF_SERVICE]: UpdatePlaceOfServiceDialog,
    [ActionDialogs.CHANGE_NDC]: ChangeNdcDialog,
    [ActionDialogs.UNMANAGE_TREATMENT]: UnmanageTreatmentDialog,
    [ActionDialogs.REMANAGE_TREATMENT]: RemanageTreatmentDialog,
    [ActionDialogs.DISCONTINUED_THERAPY]: DiscontinuedTherapyTreatmentDialog,
    [ActionDialogs.REINITIATE_THERAPY]: ReinitiateTherapyTreatmentDialog,
    [ActionDialogs.UPDATE_NEEDS_BY_DATE]: UpdateNeedsByDateDialog,
    [ActionDialogs.BLOCKLIST_TREATMENT]: BlocklistTreatmentDialog,
    [ActionDialogs.REMOVE_FROM_BLOCKLIST]: RemoveFromBlocklistDialog,
};

const GRANDFATHERED_ALWAYS_DISABLED_DIALOG_KEYS = new Set([
    ActionDialogs.ADD_UPDATE_LOCK,
    ActionDialogs.REMOVE_LOCK,
]);

const GRANDFATHERED_OVERRIDABLE_DIALOG_KEYS = new Set([
    ActionDialogs.UPDATE_ACTIVE_REFERRAL_DATA,
    ActionDialogs.UNMANAGE_TREATMENT,
    ActionDialogs.DISCONTINUED_THERAPY,
]);

const defaultBackToDestination = {
    route: AppRoutes.treatmentSearch,
    text: 'Treatment Search',
};

const currentLocation = {
    text: 'Patient Treatment Details',
};

export const TreatmentDetailPage = () => {
    let { payerId, treatmentId } = useParams();
    [payerId, treatmentId] = [decodeURIComponent(payerId), decodeURIComponent(treatmentId)];
    const { user } = useAuth0();
    const location = useLocation();
    const navigate = useNavigate();
    const {
        agoraEnhancedEligibility,
        showTestClaimsAgora,
        showReferralCreationDecisionsAgora,
        showChangeNdcActionAgora: showChangeNdcActionFeatureFlagOn,
        enableGrandfatheredTreatmentActionsOverride,
    } = useFlags();
    const { backToDestination, goBack } = useGoBack(defaultBackToDestination);
    const [isNotificationSettingsOpen, setIsNotificationSettingsOpen] = useState(false);

    const {
        data: treatmentDetails,
        isError: getTreatmentDetailsError,
        isFetching: treatmentFetching,
        refetch: refetchTreatment,
    } = useGetTreatmentDetailsQuery({
        payerId,
        treatmentId,
    });

    const { data: watches = [] } = useGetWatchesQuery();
    const existingWatch = watches.find(
        (watch) => watch.resource === WatchResources.TREATMENT && watch.resourceId === treatmentId,
    );
    const isWatching = !!existingWatch;
    const { memberDetails, refetch: refetchMemberDetails } =
        useScopeAssessorMemberDetails(treatmentDetails);

    useEffect(() => {
        if (getTreatmentDetailsError) {
            goBack();
        }
    }, [goBack, getTreatmentDetailsError]);

    const {
        data: opportunityDetails,
        isFetching: opportunityFetching,
        refetch: refetchOpportunity,
    } = useGetOpportunityByTreatmentQuery(treatmentId);
    const { treatmentStatus, isActive, isBlocklisted, blocklistedReason } = treatmentDetails ?? {};
    const activeReferralIsPharmacyBenefit =
        treatmentDetails?.activeReferral?.benefitTypeRaw === BenefitTypes.PHARMACY;

    const { data: segments } = useGetPayerSegmentsQuery();
    const { payerSegmentMap } = segments ?? {};
    const { data: products } = useGetAllProductServicesQuery();
    const { productServiceMap } = products ?? {};

    const isPharmacyBenefit = treatmentDetails?.benefitType === BenefitTypes.PHARMACY;
    const showLocksTab =
        isPharmacyBenefit &&
        (isActive || treatmentStatus === TreatmentStatusIndicator.Statuses.UNMANAGED);
    const showPBMLockMutations = isPharmacyBenefit && isActive;
    const { data: pbmLockStatus, refetch: refetchPBMLockStatus } = useGetPBMLockStatusQuery(
        {
            payerId,
            treatmentId,
        },
        {
            skip: !showPBMLockMutations,
        },
    );

    const { data: claims, isFetching } = useGetClaimsForMemberDrugQuery(
        {
            payerId: treatmentDetails?.payerId,
            memberId: memberDetails?.memberId,
            patientDob: memberDetails?.dateOfBirth,
            patientFirstName: memberDetails?.firstName,
            productServiceOid: treatmentDetails?.productServiceOid,
        },
        { skip: !(memberDetails && treatmentDetails) },
    );
    const { data: rejectedClaims, isFetching: rejectedClaimsFetching } =
        useGetRejectedClaimsForTreatmentQuery(
            payerId && treatmentId ? { payerId, treatmentId } : skipToken,
        );
    const { data: testClaims, isFetching: testClaimsFetching } = useGetTestClaimsForTreatmentQuery(
        payerId && treatmentId && treatmentDetails?.segmentId
            ? { payerId, treatmentId, segmentId: treatmentDetails.segmentId }
            : skipToken,
    );
    const treatmentClaims = useMemo(
        () => [
            ...(claims ?? []),
            ...(rejectedClaims ?? []),
            ...(testClaims ?? []).map(mapTestClaimForGrid),
        ],
        [claims, rejectedClaims, testClaims],
    );

    const { data: needsByDateData } = useGetNeedsByDateQuery(
        {
            treatmentId: treatmentDetails?.treatmentId,
        },
        {
            skip: !(isActive && activeReferralIsPharmacyBenefit),
        },
    );

    const showTreatmentBlocklistAction = !isBlocklisted && !isActive;

    const showRemoveFromBlocklistAction = isBlocklisted && !isActive;

    const showChangeNdcAction =
        showChangeNdcActionFeatureFlagOn && !treatmentDetails?.activeReferral;

    const showRemanageTreatmentAction =
        treatmentStatus === TreatmentStatusIndicator.Statuses.UNMANAGED && !isBlocklisted;

    const showReinitiateTherapyAction =
        treatmentStatus === TreatmentStatusIndicator.Statuses.DISCONTINUED && !isBlocklisted;

    const [actionDialogToShow, setActionDialogToShow] = useState();
    const ActiveActionDialog = ActionDialogComponentMap[actionDialogToShow];
    const closeActionDialog = () => setActionDialogToShow();

    const onTabNavigate = (tab) =>
        navigate(
            {
                ...location,
                hash: tab.replace(/\s/g, '-').toLocaleLowerCase(),
            },
            { state: location.state, replace: true },
        );
    const locksTabTitle = 'Locks';

    const isGrandfatheredTreatment = !!treatmentDetails?.activeReferral?.grandfathered;
    const transitionReferralId =
        opportunityDetails?.referralId ?? getCurrentReferralId(treatmentDetails?.referralList);
    const { data: transitions } = useGetReferralTransitionInfoQuery(
        payerId && transitionReferralId ? { payerId, referralId: transitionReferralId } : skipToken,
    );
    const showTherapyTransitionMessage = shouldShowTherapyTransitionMessage(
        transitions,
        productServiceMap,
    );

    // Order reflects order in dropdown menu
    const menuOptionsAndConditions = [
        {
            type: ActionDialogs.UPDATE_ACTIVE_REFERRAL_DATA,
            isVisible: isActive && treatmentDetails?.activeReferral,
        },
        {
            type: ActionDialogs.UPDATE_MEMBER_DATA,
            isVisible: isActive,
        },
        {
            type: ActionDialogs.UPDATE_PLACE_OF_SERVICE,
            isVisible: treatmentStatus !== TreatmentStatusIndicator.Statuses.DISCONTINUED,
        },
        {
            type: ActionDialogs.CHANGE_NDC,
            isVisible: showChangeNdcAction,
        },
        {
            type: ActionDialogs.UPDATE_NEEDS_BY_DATE,
            isVisible: isActive && activeReferralIsPharmacyBenefit,
        },
        {
            type: ActionDialogs.ADD_UPDATE_LOCK,
            isVisible: showPBMLockMutations,
        },
        {
            isVisible: showLocksTab,
            text: 'Check Lock Status',
            onItemClick: () => onTabNavigate(locksTabTitle),
        },
        {
            type: ActionDialogs.REMOVE_LOCK,
            isVisible: showPBMLockMutations,
        },
        {
            type: ActionDialogs.BLOCKLIST_TREATMENT,
            isVisible: showTreatmentBlocklistAction,
        },
        {
            type: ActionDialogs.REMOVE_FROM_BLOCKLIST,
            isVisible: showRemoveFromBlocklistAction,
        },
        {
            type: ActionDialogs.UNMANAGE_TREATMENT,
            isVisible: isActive,
        },
        {
            type: ActionDialogs.REMANAGE_TREATMENT,
            isVisible: showRemanageTreatmentAction,
        },
        {
            type: ActionDialogs.DISCONTINUED_THERAPY,
            isVisible: isActive,
        },
        {
            type: ActionDialogs.REINITIATE_THERAPY,
            isVisible: showReinitiateTherapyAction,
        },
    ];

    const menuItems = menuOptionsAndConditions
        .filter(({ isVisible }) => isVisible)
        .map((entry) => ({
            text: entry.text ?? ActionDialogComponentMap[entry.type].Title,
            onItemClick: entry.onItemClick ?? (() => setActionDialogToShow(entry.type)),
            ...(isGrandfatheredTreatment &&
                entry.type &&
                (GRANDFATHERED_ALWAYS_DISABLED_DIALOG_KEYS.has(entry.type) ||
                    (GRANDFATHERED_OVERRIDABLE_DIALOG_KEYS.has(entry.type) &&
                        !enableGrandfatheredTreatmentActionsOverride)) && {
                    disabled: true,
                    disabledReason: GRANDFATHERED_TREATMENT_ACTION_DISABLED_REASON,
                }),
        }));

    return (
        <>
            {ActiveActionDialog && (
                <ActiveActionDialog
                    isOpen
                    onClose={closeActionDialog}
                    treatmentDetails={treatmentDetails}
                    memberDetails={memberDetails}
                    lockDetails={pbmLockStatus}
                    username={user?.name}
                    submitCallback={() => {
                        refetchTreatment();
                        refetchOpportunity();
                        if (showPBMLockMutations) {
                            refetchPBMLockStatus();
                        }
                    }}
                />
            )}
            {treatmentDetails && (
                <NotificationSettingsDialog
                    isOpen={isNotificationSettingsOpen}
                    onClose={() => setIsNotificationSettingsOpen(false)}
                    treatmentDetails={treatmentDetails}
                    existingWatch={existingWatch}
                />
            )}
            <FormHeaderAndTabbedContent
                rowOneContentHeader={
                    <>
                        <ActionButtonPageTitle
                            label={backToDestination.text}
                            title={currentLocation.text}
                            onClick={goBack}
                        />
                        <div
                            className="flex__gap--small"
                            style={{ marginLeft: 'auto', alignItems: 'center' }}
                        >
                            <IconButton
                                icon={IconNames.EYE_OPEN}
                                title={
                                    isWatching ? 'Notification settings' : 'Watch this treatment'
                                }
                                intent={
                                    isWatching ? IconButton.Intent.PRIMARY : IconButton.Intent.NONE
                                }
                                onClick={() => setIsNotificationSettingsOpen(true)}
                            />
                            {menuItems.length > 0 && (
                                <ButtonWithDropdownMenu
                                    menuItems={menuItems}
                                    intent={ButtonWithDropdownMenu.Intent.PRIMARY}
                                    label="Actions"
                                />
                            )}
                        </div>
                    </>
                }
                rowThreeContentHeader={
                    !treatmentFetching && (
                        <>
                            <FormTitleLabelValuePair
                                label={formatNameLastFirst(
                                    treatmentDetails?.lastName,
                                    treatmentDetails?.firstName,
                                )}
                                value={formatAddress(
                                    memberDetails?.memberCity,
                                    memberDetails?.memberState,
                                    memberDetails?.memberZip,
                                )}
                            />
                            <HeaderLabelValuePair
                                label="Product"
                                value={productServiceMap?.[treatmentDetails?.productServiceOid]}
                            />
                            <HeaderLabelValuePair
                                label="Health Plan"
                                value={payerSegmentMap?.[treatmentDetails?.segmentId]?.segment_name}
                            />
                            <HeaderLabelValuePair
                                label="Secondary Coverage"
                                value={memberDetails?.hasSecondaryCoverage}
                            />
                            <HeaderLabelValuePair
                                label="Next Fill Needs By Date"
                                value={formatDate(needsByDateData?.needsByDate)}
                            />
                            <HeaderLabelValuePair
                                label="Status"
                                value={
                                    <TreatmentStatusIndicator
                                        status={treatmentDetails?.treatmentStatus}
                                    />
                                }
                            />
                        </>
                    )
                }
                rowFourContentHeader={
                    (isGrandfatheredTreatment ||
                        (isBlocklisted && blocklistedReason) ||
                        showTherapyTransitionMessage) && (
                        <div
                            className="flex__gap--medium"
                            style={{ flexDirection: 'column', width: '100%' }}
                        >
                            {isGrandfatheredTreatment && (
                                <InlineMessage
                                    intent={InlineMessage.Intent.INFO}
                                    wrapText
                                    label={GRANDFATHERED_REFERRAL_CONTEXT_BANNER}
                                />
                            )}
                            {isBlocklisted && blocklistedReason && (
                                <InlineMessage
                                    intent={InlineMessage.Intent.WARNING}
                                    wrapText
                                    label={`Blocklisted: ${blocklistedReason}`}
                                />
                            )}
                            {showTherapyTransitionMessage && (
                                <TherapyTransitionInlineMessages
                                    payerId={payerId}
                                    referralId={transitionReferralId}
                                    backToDestination={currentLocation}
                                    viewContext={TransitionViewContext.TREATMENT}
                                />
                            )}
                        </div>
                    )
                }
                activeTab={location.hash.replace(/^#/, '').replace(/-/g, ' ')}
                onTabSelect={onTabNavigate}
                tabs={[
                    {
                        title: 'Overview',
                        mainContent: (
                            <TreatmentDetailOverview
                                opportunityDetails={opportunityDetails}
                                opportunityFetching={opportunityFetching}
                                treatmentDetails={treatmentDetails}
                                treatmentFetching={treatmentFetching}
                                memberDetails={memberDetails}
                                currentLocation={currentLocation}
                            />
                        ),
                    },
                    {
                        title: 'Claims',
                        mainContent: (
                            <TreatmentClaimsTab
                                claims={treatmentClaims}
                                claimsLoading={
                                    isFetching || rejectedClaimsFetching || testClaimsFetching
                                }
                                treatmentProductServiceOid={treatmentDetails?.productServiceOid}
                                treatmentIsBrand={treatmentDetails?.isBrand}
                            />
                        ),
                    },
                    ...(showTestClaimsAgora
                        ? [
                              {
                                  title: 'FMH Test Claims',
                                  mainContent: (
                                      <TestClaims
                                          payerId={payerId}
                                          treatmentId={treatmentId}
                                          segmentId={treatmentDetails?.segmentId}
                                      />
                                  ),
                              },
                          ]
                        : []),
                    ...(showReferralCreationDecisionsAgora
                        ? [
                              {
                                  title: 'Referral Creation',
                                  mainContent: (
                                      <ReferralCreationDecisionsTab
                                          payerId={payerId}
                                          treatmentId={treatmentId}
                                          currentLocation={currentLocation}
                                      />
                                  ),
                              },
                          ]
                        : []),
                    {
                        title: 'Audit History',
                        mainContent: <TreatmentAuditHistory treatmentId={treatmentId} />,
                    },
                    ...(showLocksTab
                        ? [
                              {
                                  title: locksTabTitle,
                                  mainContent: (
                                      <LocksTab
                                          treatmentId={treatmentId}
                                          payerId={payerId}
                                          activeReferralPharmacyNPI={
                                              treatmentDetails?.activeReferral?.servicingSupplierNpi
                                          }
                                      />
                                  ),
                              },
                          ]
                        : []),
                    ...(agoraEnhancedEligibility
                        ? [
                              {
                                  title: 'Enrollment',
                                  mainContent: (
                                      <EnrollmentTab
                                          payerId={payerId}
                                          segmentId={treatmentDetails?.segmentId}
                                          memberDetails={memberDetails}
                                          refetchMemberDetails={refetchMemberDetails}
                                      />
                                  ),
                              },
                          ]
                        : []),
                ]}
            />
        </>
    );
};
